import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ErrosWrapper, TextLink } from "../../SharableStyles/FormStyle";
import Button from "../sharable/ActionButton";
import Loading from "../sharable/Loading";
import Logo from "../sharable/Logo";
import OAuth from "../sharable/OAuth";
import { redirectAfterAuthOrDefault } from "../../services/Utils";
import { setTokenCookie } from "../../TokenCookie";
import FormMessage from "../sharable/FormMessage.jsx";
import Help from "../sharable/Help.jsx";
import PasswordToggle from "../sharable/hooks/PasswordToggle.jsx";
import useFormValidation from "../sharable/hooks/useFormValidation.jsx";
import { emailRegex } from "../../services/Utils";
import {
  PageWrapper,
  CompactHeader,
  CenteredArea,
  FormCard,
  FormCardHeader,
  FieldGroup,
  StyledInput,
  PasswordFieldWrapper,
  ForgotPasswordLink,
  OrDivider,
  FooterNote,
} from "./Login.styles";

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

export default function LoginView() {
  const navigate = useNavigate();
  const [logging, setLogging] = useState();
  const [params] = useSearchParams();
  const [loginError, setLoginError] = useState();
  const [showPassword, setShowPassword] = useState();
  const [searchParams, setSearchParams] = useSearchParams();
  const { values, errors, handleChange, validate } = useFormValidation({
    email: {
      label: "email",
      required: true,
      regex: emailRegex,
      customMessages: {
        regex: "Esse email é inválido.",
      },
    },
    password: {
      label: "senha",
      required: true,
      minLength: 6,
      maxLength: 100,
      customMessages: {
        minLength: "Sua senha deve ter no mínimo 6 dígitos.",
        maxLength: "Sua senha deve ter no máximo 100 dígitos.",
      },
    },
  });

  /**
   * Remove search params on page reload
   */

  useEffect(() => {
    navigate(window.location.pathname, { replace: true });
  }, []);

  useEffect(() => {
    const p = params.get("refresh");
    const refresh = !p ? "" : p.toLocaleLowerCase();
    try {
      const tkn = Cookies.get("tkn");
      if (!tkn || tkn === "") return;
      const info = jwtDecode(tkn);
      if (info.exp * 1000 > new Date().valueOf() && refresh !== "true") {
        navigate("/encartes");
        return;
      } else {
        setLoginError("Sua sessão expirou. Entre novamente!");
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  const logIn = async (e) => {
    setLogging(true);
    const body = {
      email: values.email,
      password: values.password,
    };

    let res;
    try {
      res = await fetch(`${BASE_URL}auth/basic/login`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (err) {
      setLogging(false);
      setLoginError(
        "Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.",
      );
      return;
    }
    setLogging(false);
    if (res.ok) {
      const token = await res.text();
      setTokenCookie(token);
      redirectAfterAuthOrDefault(navigate);
    } else if (res.status === 404) setLoginError(404);
    else if (res.status === 401) setLoginError(401);
    else if (res.status === 403) {
      setLoginError(403);

      setSearchParams((prev) => {
        const newSearchParams = new URLSearchParams(prev);
        newSearchParams.set("login-type", "google");
        return newSearchParams;
      });
    } else if (res.status === 429) {
      setLoginError(
        "Você tentou entrar muitas vezes. Aguarde alguns minutos e tente novamente. Se tiver com dúvida, entre em contato com o suporte.",
      );
    } else {
      setLoginError("Não foi possível entrar. Tente novamente.");
    }
  };

  return (
    <PageWrapper>
      <Help showGroup={false} showYoutube={false} />
      <CompactHeader>
        <Logo navigable={true} size={9} />
      </CompactHeader>
      <CenteredArea>
        <FormCard>
          {logging && <Loading />}
          <FormCardHeader>
            <h1>Bem-vindo de volta!</h1>
            <p>Entre na sua conta e continue criando seus encartes.</p>
          </FormCardHeader>

          {loginError === 404 && (
            <FormMessage
              message={"Não existe nenhuma conta vinculada a esse email"}
              textLink={"Crie uma conta aqui"}
              linkTo={"/registrar"}
            />
          )}
          {loginError === 403 && (
            <FormMessage
              message={
                'Você se registrou com sua conta Google. Clique em "Entrar com Google"'
              }
            />
          )}
          {loginError === 401 && (
            <FormMessage
              message={"Senha incorreta, tente novamente. "}
              textLink={"Esqueceu sua senha? Altere aqui"}
              linkTo={
                "/recuperar-conta?email=" + values.email + "&step=request_code"
              }
            />
          )}

          {typeof loginError === "string" && (
            <FormMessage message={loginError} />
          )}

          {searchParams.get("login-type") !== "google" && (
            <>
              <FieldGroup>
                <label htmlFor="email">E-mail</label>
                <StyledInput
                  id="email"
                  placeholder="Email"
                  type="email"
                  autoComplete="email"
                  value={values.email || ""}
                  onInput={(e) => handleChange("email", e.target.value)}
                  $hasError={errors.email?.length > 0}
                />
                {errors.email &&
                  errors.email.map((msg, i) => (
                    <FormMessage
                      message={msg}
                      key={i}
                      backgroundColor="transparent"
                      align="left"
                    />
                  ))}
              </FieldGroup>
              <FieldGroup>
                <label htmlFor="password">Senha</label>
                <StyledInput
                  id="password"
                  placeholder="Senha"
                  type={`${showPassword === true ? "text" : "password"}`}
                  autoComplete="current-password"
                  value={values.password || ""}
                  onInput={(e) => handleChange("password", e.target.value)}
                  $hasError={errors.password?.length > 0}
                />

                <PasswordFieldWrapper>
                  <PasswordToggle
                    password={values.password}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                  />
                </PasswordFieldWrapper>

                <ErrosWrapper>
                  {errors.password &&
                    errors.password.map((msg, i) => (
                      <FormMessage
                        message={msg}
                        key={i}
                        backgroundColor="transparent"
                        align="left"
                      />
                    ))}
                </ErrosWrapper>
              </FieldGroup>

              <ForgotPasswordLink>
                <Link
                  to={
                    "/recuperar-conta?email=" +
                    (values.email || "") +
                    "&step=request_code"
                  }
                >
                  Esqueceu sua senha?
                </Link>
              </ForgotPasswordLink>

              <Button
                $fullWidth
                text={"Entrar"}
                action={(e) => {
                  setLoginError(undefined);
                  if (validate()) logIn();
                }}
              />
              <OrDivider>ou</OrDivider>
            </>
          )}
          <OAuth
            action={"login"}
            setLogging={setLogging}
            setErrorMessage={setLoginError}
            buttonTxt={"Entrar com Google"}
            message={"Entre com sua conta Google"}
          />
          <TextLink>
            Ainda não possui uma conta? <Link to="/registrar">Criar conta</Link>
            .
          </TextLink>
        </FormCard>
      </CenteredArea>
      <FooterNote>
        © {new Date().getFullYear()} OfertasExpress. Todos os direitos
        reservados.
      </FooterNote>
    </PageWrapper>
  );
}
